/**
 * Enhanced Modal Toggle Action
 * Supports show/hide/toggle, up/down position, custom width/height, and selector targeting.
 * Usage: window.dispatchEvent(new CustomEvent('arc:action:toggleModal', { detail: { action: 'show', selector: '#arc-modal', direction: 'up', width: '400px', height: 'auto' } }))
 */

export const runModalToggle = async (data = {}) => {
  try {
    const {
      action = 'toggle',
      selector,
      eventId,
      targetEventId,
      direction = 'up', // 'up' or 'down'
      height = 'auto',
      width = '600px',
      message = '',
      type = 'info',
      duration = 3000
    } = data;

    // Use targetEventId or eventId as selector if provided, otherwise default to #arc-modal
    const finalSelector = selector || (targetEventId ? `#${targetEventId}` : eventId ? `#${eventId}` : '#arc-modal');

    // Debounce to prevent double execution
    const now = Date.now();
    if (runToggleModal._lastCall && now - runToggleModal._lastCall < 300) {
      return { success: true, message: 'Debounced duplicate call' };
    }
    runToggleModal._lastCall = now;

    // Validate action
    const validActions = ['show', 'hide', 'toggle'];
    if (!validActions.includes(action)) {
      throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
    }

    // Find modal element(s)
    const modals = document.querySelectorAll(finalSelector);
    if (!modals.length) {
      throw new Error(`No modal found for selector: ${finalSelector}`);
    }

    modals.forEach((modal) => {
      // Set base styles
      modal.style.position = 'fixed';
      modal.style.left = '50%';
      modal.style.top = '50%';
      modal.style.height = height;
      modal.style.maxWidth = width;
      modal.style.maxHeight = '100dvh';
      modal.style.boxShadow = '0 0 24px rgba(0,0,0,0.15)';
      modal.style.zIndex = '10001';
      modal.style.transition = 'opacity 0.4s cubic-bezier(.4,0,.2,1), transform 0.4s cubic-bezier(.4,0,.2,1)';
      modal.style.display = 'block';
      modal.style.opacity = 0;
      modal.style.pointerEvents = 'auto';

      // Set direction (small translate, not off-screen)
      const translateDistance = '50px';
      if (direction === 'up') {
        modal.style.transform = 'translate(-50%, calc(-50% + ' + translateDistance + '))';
      } else {
        modal.style.transform = 'translate(-50%, calc(-50% - ' + translateDistance + '))';
      }

      // Helper: is open? (read current DOM state)
      const isOpen = modal.getAttribute('aria-hidden') === 'false';

      // Action logic with requestAnimationFrame and forced reflow
      const doShow = () => {
        if (modal.dataset.isAnimating === 'true' || modal.getAttribute('aria-hidden') === 'false') {
          return;
        }
        modal.dataset.isAnimating = 'true';

        // Create backdrop
        const backdropId = `modal-backdrop-${Date.now()}`;
        let backdrop = document.getElementById(backdropId);

        if (!backdrop) {
          backdrop = document.createElement('div');
          backdrop.id = backdropId;
          backdrop.style.position = 'fixed';
          backdrop.style.top = '0';
          backdrop.style.left = '0';
          backdrop.style.width = '100vw';
          backdrop.style.height = '100vh';
          backdrop.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          backdrop.style.zIndex = '10000';
          backdrop.style.opacity = '0';
          backdrop.style.transition = 'opacity 0.4s cubic-bezier(.4,0,.2,1)';
          backdrop.style.cursor = 'pointer';

          // Add click handler with animation check
          const handleBackdropClick = (e) => {
            if (modal.dataset.isAnimating === 'true') {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            doHide();
          };

          backdrop.addEventListener('click', handleBackdropClick);
          modal.dataset.backdropId = backdropId;
          modal._backdropHandler = handleBackdropClick;
          document.body.appendChild(backdrop);
        }

        // Start slightly offset and transparent
        if (direction === 'up') {
          modal.style.transform = 'translate(-50%, calc(-50% + ' + translateDistance + '))';
        } else {
          modal.style.transform = 'translate(-50%, calc(-50% - ' + translateDistance + '))';
        }
        modal.style.opacity = 0;
        modal.style.display = 'block';
        modal.setAttribute('aria-hidden', 'false');
        modal.setAttribute('tabindex', '0');
        void modal.offsetWidth;

        requestAnimationFrame(() => {
          backdrop.style.opacity = '1';
          modal.style.opacity = 1;
          modal.style.transform = 'translate(-50%, -50%)';
        });

        setTimeout(() => {
          modal.dataset.isAnimating = 'false';
        }, 400);
      };

      const doHide = () => {
        if (modal.dataset.isAnimating === 'true' || modal.getAttribute('aria-hidden') === 'true') {
          return;
        }
        modal.dataset.isAnimating = 'true';

        const backdropId = modal.dataset.backdropId;
        const backdrop = document.getElementById(backdropId);

        requestAnimationFrame(() => {
          if (backdrop) {
            backdrop.style.opacity = '0';
          }
          if (direction === 'up') {
            modal.style.transform = 'translate(-50%, calc(-50% + ' + translateDistance + '))';
          } else {
            modal.style.transform = 'translate(-50%, calc(-50% - ' + translateDistance + '))';
          }
          modal.style.opacity = 0;
        });

        setTimeout(() => {
          if (document.activeElement && modal.contains(document.activeElement)) {
            document.activeElement.blur();
          }
          modal.setAttribute('aria-hidden', 'true');
          modal.setAttribute('tabindex', '-1');
          if (backdrop && modal._backdropHandler) {
            backdrop.removeEventListener('click', modal._backdropHandler);
            backdrop.remove();
          }
          delete modal.dataset.backdropId;
          delete modal._backdropHandler;
          delete modal.dataset.isAnimating;
          modal.style.display = 'none';
        }, 400);
      };

      if (action === 'show') {
        doShow();
      } else if (action === 'hide') {
        doHide();
      } else if (action === 'toggle') {
        isOpen ? doHide() : doShow();
      }
    });

    return {
      success: true,
      message: `Modal ${action} executed` + (modals.length > 1 ? ` (${modals.length} elements)` : ''),
      data: { action, selector: finalSelector, direction, width, height, message, type }
    };
  } catch (error) {
    console.error('Error toggling modal:', error);
    return {
      success: false,
      message: error.message,
      error
    };
  }
};

export default runModalToggle;
